// ═══════════════════════════════════════════════════════════════
// 10Batch Premium Product Card — Interactive Features
// Handles gallery navigation, quantity selection, and add-to-cart
// ═══════════════════════════════════════════════════════════════

(function () {
  'use strict';

  // Initialize all product cards on the page
  function init() {
    const cards = document.querySelectorAll('[data-tenb-product-card]');
    if (cards.length === 0) return;

    cards.forEach(card => {
      initCard(card);
    });
  }

  function initCard(card) {
    const form = card.querySelector('form[action*="cart"]');
    if (!form) return;

    const state = {
      currentMediaId: null,
      quantity: 1,
      isAdding: false,
      form: form,
      card: card
    };

    setupGallery(card, state);
    setupQuantitySelector(card, state);
    setupAddToCart(card, state);
  }

  // ────────────────────────────────────────────────────────────────
  // GALLERY NAVIGATION
  // ────────────────────────────────────────────────────────────────

  function setupGallery(card, state) {
    const mainScroller = card.querySelector('[data-tenb-main-scroller]');
    const thumbs = card.querySelectorAll('[data-tenb-thumb]');
    const prevBtn = card.querySelector('[data-tenb-thumb-prev]');
    const nextBtn = card.querySelector('[data-tenb-thumb-next]');
    const thumbsContainer = card.querySelector('[data-tenb-thumbs-container]');

    if (!mainScroller || thumbs.length === 0) return;

    // Set initial media ID
    const firstSlide = mainScroller.querySelector('[data-tenb-slide]');
    if (firstSlide) {
      state.currentMediaId = firstSlide.dataset.mediaId;
    }

    // Thumbnail click handler
    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const mediaId = thumb.dataset.mediaId;
        goToMedia(mainScroller, mediaId, thumbs, state);
      });
    });

    // Thumbnail scroll navigation
    if (prevBtn && thumbsContainer) {
      prevBtn.addEventListener('click', () => {
        thumbsContainer.scrollBy({ left: -112, behavior: 'smooth' });
      });
    }

    if (nextBtn && thumbsContainer) {
      nextBtn.addEventListener('click', () => {
        thumbsContainer.scrollBy({ left: 112, behavior: 'smooth' });
      });
    }

    // Sync main scroller with thumb selection on manual scroll
    if (mainScroller) {
      mainScroller.addEventListener(
        'scroll',
        debounce(() => {
          const scrollLeft = mainScroller.scrollLeft;
          const slides = mainScroller.querySelectorAll('[data-tenb-slide]');
          let closestSlide = slides[0];
          let closestDistance = Math.abs(closestSlide.offsetLeft - scrollLeft);

          slides.forEach(slide => {
            const distance = Math.abs(slide.offsetLeft - scrollLeft);
            if (distance < closestDistance) {
              closestDistance = distance;
              closestSlide = slide;
            }
          });

          if (closestSlide) {
            const mediaId = closestSlide.dataset.mediaId;
            updateThumbSelection(thumbs, mediaId);
            state.currentMediaId = mediaId;
          }
        }, 100)
      );
    }
  }

  function goToMedia(scroller, mediaId, thumbs, state) {
    const targetSlide = scroller.querySelector(`[data-media-id="${mediaId}"]`);
    if (!targetSlide) return;

    scroller.scrollTo({
      left: targetSlide.offsetLeft,
      behavior: 'smooth'
    });

    updateThumbSelection(thumbs, mediaId);
    state.currentMediaId = mediaId;
  }

  function updateThumbSelection(thumbs, mediaId) {
    thumbs.forEach(thumb => {
      const isSelected = thumb.dataset.mediaId === mediaId;
      thumb.classList.toggle('is-active', isSelected);
      thumb.setAttribute('aria-pressed', isSelected.toString());
    });
  }

  // ────────────────────────────────────────────────────────────────
  // QUANTITY SELECTOR
  // ────────────────────────────────────────────────────────────────

  function setupQuantitySelector(card, state) {
    const decreaseBtn = card.querySelector('[data-qty-decrease]');
    const increaseBtn = card.querySelector('[data-qty-increase]');
    const input = card.querySelector('[data-qty-input]');

    if (!input) return;

    if (decreaseBtn) {
      decreaseBtn.addEventListener('click', e => {
        e.preventDefault();
        const current = parseInt(input.value) || 1;
        if (current > 1) {
          input.value = current - 1;
          state.quantity = current - 1;
        }
      });
    }

    if (increaseBtn) {
      increaseBtn.addEventListener('click', e => {
        e.preventDefault();
        const current = parseInt(input.value) || 1;
        input.value = current + 1;
        state.quantity = current + 1;
      });
    }

    input.addEventListener('change', () => {
      let value = parseInt(input.value) || 1;
      if (value < 1) value = 1;
      input.value = value;
      state.quantity = value;
    });
  }

  // ────────────────────────────────────────────────────────────────
  // ADD TO CART
  // ────────────────────────────────────────────────────────────────

  function setupAddToCart(card, state) {
    const button = card.querySelector('[data-tenb-add-to-cart]');
    const feedback = card.querySelector('[data-tenb-feedback]');

    if (!button) return;

    button.addEventListener('click', e => {
      e.preventDefault();

      if (state.isAdding || button.disabled) return;

      state.isAdding = true;
      button.dataset.loading = 'true';
      showFeedback(feedback, '', '');

      const form = state.form;
      const formData = new FormData(form);

      // Ensure quantity is set
      formData.set('quantity', state.quantity);

      // Use fetch API for AJAX add-to-cart
      fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
        .then(response => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .then(data => {
          showFeedback(feedback, `✓ Added to cart!`, 'is-success');
          button.dataset.loading = 'false';
          state.isAdding = false;

          // Update cart count in header if available
          updateCartCount();

          // Open cart drawer if enabled (future enhancement)
          // For now, just show success message
          setTimeout(() => {
            clearFeedback(feedback);
          }, 3000);
        })
        .catch(error => {
          console.error('Add to cart error:', error);
          showFeedback(
            feedback,
            'Unable to add to cart. Please try again.',
            'is-error'
          );
          button.dataset.loading = 'false';
          state.isAdding = false;

          setTimeout(() => {
            clearFeedback(feedback);
          }, 4000);
        });
    });
  }

  function showFeedback(element, message, className) {
    if (!element) return;
    element.textContent = message;
    element.className = `tenb-pcard__feedback is-visible ${className}`;
  }

  function clearFeedback(element) {
    if (!element) return;
    element.textContent = '';
    element.className = 'tenb-pcard__feedback';
  }

  // ────────────────────────────────────────────────────────────────
  // CART COUNT UPDATE
  // ────────────────────────────────────────────────────────────────

  function updateCartCount() {
    // Update any cart badge/count in the header if it exists
    // This assumes a header component with a cart count element
    const cartCount = document.querySelector('[data-cart-count]');
    if (cartCount) {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          cartCount.textContent = cart.item_count;
        })
        .catch(() => {
          // Silently fail if cart.js is not available
        });
    }
  }

  // ────────────────────────────────────────────────────────────────
  // UTILITY FUNCTIONS
  // ────────────────────────────────────────────────────────────────

  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // ────────────────────────────────────────────────────────────────
  // INIT ON DOM READY
  // ────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Support for Shopify theme editor live updates
  if (window.Shopify && window.Shopify.designSystem) {
    document.addEventListener('shopify:section:load', init);
    document.addEventListener('shopify:section:select', init);
  }
})();
